import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["statusPanel", "statusText", "percentText", "progressBar"]
  static values = {
    autoStart: Boolean,
    streamUrl: String,
    streamKey: String
  }

  connect() {
    this.running = false
    this.completed = false
    this.progressPercent = 0
    this.chunkCount = 0
    this.lastAnalysisLength = 0
    this.lastReasoningLength = 0
    this.streamedAnalysis = ""
    this.streamedReasoning = ""
    this.lastFingerprint = ""
    this.awaitingFirstStreamUpdate = false
    this.streamRequestInFlight = false
    this.streamRequestController = null
    this.boundSyncFromStream = this.syncFromStream.bind(this)

    this.observer = new MutationObserver(this.boundSyncFromStream)
    this.observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true
    })

    if (this.hasAutoStartValue && this.autoStartValue) {
      this.prepareForStreamStart()
      this.startStreamRequest()
    }

    this.syncFromStream()
  }

  disconnect() {
    this.observer?.disconnect()
    this.streamRequestController?.abort()
  }

  submitStart() {
    this.prepareForStreamStart()
  }

  submitEnd() {
    // Stream updates drive completion state.
  }

  syncFromStream() {
    const node = document.getElementById("llm_response")
    if (!node) return

    const streaming = node.dataset.streaming === "true"
    const hasError = node.dataset.hasError === "true"
    const analysisLength = Number.parseInt(node.dataset.analysisLength || "0", 10) || 0
    const reasoningLength = Number.parseInt(node.dataset.reasoningLength || "0", 10) || 0
    const errorMessage = String(node.dataset.errorMessage || "")
    const fingerprint = `${streaming}|${hasError}|${analysisLength}|${reasoningLength}|${errorMessage}`

    if (fingerprint === this.lastFingerprint) return
    this.lastFingerprint = fingerprint

    if (this.awaitingFirstStreamUpdate) {
      if (!streaming) return
      this.awaitingFirstStreamUpdate = false
    }

    if (hasError) {
      this.running = false
      this.completed = true
      this.bumpProgressTo(100)
      this.renderStatus(errorMessage || "Analysis failed.", "danger")
      return
    }

    if (streaming) {
      if (!this.running) {
        this.running = true
        this.completed = false
        this.bumpProgressTo(20)
      }

      if (analysisLength > this.lastAnalysisLength) {
        this.chunkCount += 1
        this.bumpProgressFromChunk(analysisLength - this.lastAnalysisLength)
      } else {
        this.bumpProgressTo(35)
      }
      this.lastAnalysisLength = analysisLength

      const baseStatus = analysisLength > 0
        ? "Receiving streamed response..."
        : reasoningLength > 0
          ? "Reasoning over analysis data..."
          : "Connecting to streaming endpoint..."
      const status = this.chunkCount > 0 ? `${baseStatus} (${this.chunkCount} chunks)` : baseStatus
      this.renderStatus(status, "info")
      return
    }

    if (analysisLength > 0) {
      this.running = false
      this.completed = true
      this.lastAnalysisLength = analysisLength
      this.bumpProgressTo(100)
      this.renderStatus("Analysis complete.", "success")
      return
    }

    if (!this.running && !this.completed) {
      this.hideStatus()
    }
  }

  bumpProgressTo(value) {
    const next = Number(value)
    if (!Number.isFinite(next)) return
    this.progressPercent = Math.max(this.progressPercent, Math.min(100, next))
  }

  bumpProgressFromChunk(chunkLength) {
    if (this.progressPercent >= 95) return

    const length = Number(chunkLength) || 0
    const increment = Math.max(0.35, Math.min(2.0, length / 180))
    this.progressPercent = Math.min(95, this.progressPercent + increment)
  }

  renderStatus(message, level) {
    if (!this.hasStatusPanelTarget || !this.hasStatusTextTarget || !this.hasPercentTextTarget || !this.hasProgressBarTarget) return

    const percent = Math.max(0, Math.min(100, Math.round(this.progressPercent)))
    const bar = this.progressBarTarget

    this.statusPanelTarget.classList.remove("d-none")
    this.statusTextTarget.textContent = String(message || "")
    this.percentTextTarget.textContent = `${percent}%`

    bar.style.width = `${percent}%`
    bar.setAttribute("aria-valuenow", String(percent))
    bar.classList.remove("bg-success", "bg-danger", "bg-warning", "progress-bar-striped", "progress-bar-animated")
    bar.classList.add(this.progressBarTone(level))
    if (level === "info" && percent < 100) {
      bar.classList.add("progress-bar-striped", "progress-bar-animated")
    }
  }

  hideStatus() {
    if (!this.hasStatusPanelTarget) return
    this.statusPanelTarget.classList.add("d-none")
  }

  progressBarTone(level) {
    switch (String(level || "")) {
      case "success":
        return "bg-success"
      case "danger":
        return "bg-danger"
      case "warning":
        return "bg-warning"
      default:
        return "bg-warning"
    }
  }

  prepareForStreamStart() {
    this.running = true
    this.completed = false
    this.progressPercent = 12
    this.chunkCount = 0
    this.lastAnalysisLength = 0
    this.lastReasoningLength = 0
    this.streamedAnalysis = ""
    this.streamedReasoning = ""
    this.lastFingerprint = ""
    this.awaitingFirstStreamUpdate = true
    this.renderStatus("Preparing analysis context...", "info")
  }

  async startStreamRequest() {
    if (this.streamRequestInFlight) return
    if (!this.hasStreamUrlValue || !this.hasStreamKeyValue) return
    if (!this.streamUrlValue || !this.streamKeyValue) return

    this.streamRequestInFlight = true
    this.streamRequestController = new AbortController()

    const payload = new URLSearchParams()
    payload.append("stream_key", this.streamKeyValue)

    try {
      const response = await fetch(this.streamUrlValue, {
        method: "POST",
        body: payload,
        headers: {
          Accept: "application/x-ndjson",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        signal: this.streamRequestController.signal
      })

      if (!response.ok) {
        let message = `Failed to start analysis stream (${response.status})`
        try {
          const data = await response.json()
          if (data?.error) message = String(data.error)
        } catch (_error) {
          // Keep fallback message.
        }
        throw new Error(message)
      }

      if (!response.body) throw new Error("Streaming response body is unavailable")
      await this.consumeNdjsonStream(response.body)
    } catch (error) {
      if (error?.name === "AbortError") return
      this.bumpProgressTo(100)
      this.renderStatus(`Failed to start analysis stream: ${error.message}`, "danger")
    } finally {
      this.streamRequestInFlight = false
      this.streamRequestController = null
    }
  }

  async consumeNdjsonStream(readable) {
    const reader = readable.getReader()
    const decoder = new TextDecoder()
    let buffer = ""

    while (true) {
      const { value, done } = await reader.read()
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

      let newlineIndex
      while ((newlineIndex = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, newlineIndex).trim()
        buffer = buffer.slice(newlineIndex + 1)
        if (line) this.handleStreamEvent(JSON.parse(line))
      }

      if (done) break
    }

    const finalLine = buffer.trim()
    if (finalLine) this.handleStreamEvent(JSON.parse(finalLine))
  }

  handleStreamEvent(event) {
    switch (String(event?.type || "")) {
      case "status":
        this.renderStatus(event.message || "Preparing analysis data...", "info")
        break
      case "reasoning":
        this.streamedReasoning += String(event.content || "")
        if (event.html) this.replaceFinalHtml(String(event.html))
        else this.renderLiveOutput()
        this.bumpProgressTo(25)
        this.renderStatus("Reasoning over analysis data...", "info")
        break
      case "token":
        this.streamedAnalysis += String(event.content || "")
        if (event.html) this.replaceFinalHtml(String(event.html))
        else this.renderLiveOutput()
        this.bumpProgressFromChunk(String(event.content || "").length)
        this.renderStatus("Receiving streamed response...", "info")
        break
      case "done":
        this.streamedAnalysis = String(event.response || this.streamedAnalysis)
        this.streamedReasoning = String(event.reasoning || this.streamedReasoning)
        if (event.html) this.replaceFinalHtml(String(event.html))
        else this.renderLiveOutput(false)
        this.running = false
        this.completed = true
        this.bumpProgressTo(100)
        this.renderStatus("Analysis complete.", "success")
        break
      case "error":
        this.renderStreamError(String(event.message || "Analysis failed."))
        break
    }
  }

  renderLiveOutput(streaming = true) {
    const node = document.getElementById("llm_response")
    if (!node) return

    node.dataset.streaming = streaming ? "true" : "false"
    node.dataset.hasError = "false"
    node.dataset.errorMessage = ""
    node.dataset.analysisLength = String(this.streamedAnalysis.length)
    node.dataset.reasoningLength = String(this.streamedReasoning.length)
    node.replaceChildren()

    if (this.streamedReasoning) {
      const details = document.createElement("details")
      details.className = "mb-3"
      details.open = streaming && !this.streamedAnalysis
      const summary = document.createElement("summary")
      summary.className = "fw-semibold"
      summary.textContent = "Model reasoning"
      const reasoning = document.createElement("pre")
      reasoning.className = "mt-2 mb-0 text-wrap"
      reasoning.style.whiteSpace = "pre-wrap"
      reasoning.textContent = this.streamedReasoning
      details.append(summary, reasoning)
      node.append(details)
    }

    if (this.streamedAnalysis) {
      const response = document.createElement("pre")
      response.className = "analysis-text mb-0 text-wrap"
      response.style.whiteSpace = "pre-wrap"
      response.textContent = this.streamedAnalysis
      node.append(response)
    } else if (streaming) {
      const pending = document.createElement("div")
      pending.className = "alert alert-info"
      pending.textContent = this.streamedReasoning ? "Preparing final response..." : "Analyzing data..."
      node.append(pending)
    }
  }

  replaceFinalHtml(html) {
    const node = document.getElementById("llm_response")
    if (!node) return

    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const replacement = template.content.firstElementChild
    if (replacement) node.replaceWith(replacement)
  }

  renderStreamError(message) {
    const node = document.getElementById("llm_response")
    if (node) {
      node.dataset.streaming = "false"
      node.dataset.hasError = "true"
      node.dataset.errorMessage = message
      node.replaceChildren()
      const alert = document.createElement("div")
      alert.className = "alert alert-danger"
      alert.setAttribute("role", "alert")
      alert.textContent = message
      node.append(alert)
    }
    this.running = false
    this.completed = true
    this.bumpProgressTo(100)
    this.renderStatus(message, "danger")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
